import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiCalendar,
  FiClock,
  FiEdit2,
  FiPlay,
  FiTrash,
  FiUser,
  FiX,
} from 'react-icons/fi';
import type { AppointmentRead } from '../../../helpers/api/appointment/appointment';
import {
  canModifyAppointment,
  canStartExamNow,
  formatAppointmentClock,
  isAppointmentPassed,
} from '../../../helpers/api/appointment/appointment';
import { Button } from '../common/Button';
import { getAppointmentStatusVisual, getPriorityVisual } from './schedulingStatus';

type Props = {
  appointment: AppointmentRead | null;
  canWrite: boolean;
  onClose: () => void;
  onStart: (appointment: AppointmentRead) => void;
  onCancel: (id: string) => void;
  onModify?: (appointment: AppointmentRead) => void;
};

export default function AppointmentDetailsDrawer({
  appointment,
  canWrite,
  onClose,
  onStart,
  onCancel,
  onModify,
}: Props) {
  const { t } = useTranslation('Scheduling');

  useEffect(() => {
    if (!appointment) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [appointment, onClose]);

  if (!appointment) return null;

  const status = getAppointmentStatusVisual(appointment, t);
  const priority = getPriorityVisual(appointment.priority, t);
  const typeKey = String(appointment.appointment_type || '').toLowerCase();
  const typeLabel = t(`modal.scanTypes.${typeKey}`, {
    defaultValue: appointment.appointment_type,
  });
  const assignee =
    [appointment.assignee_first_name, appointment.assignee_last_name]
      .filter(Boolean)
      .join(' ') || t('worklist.unassigned');
  const rawStatus = String(appointment.status).toLowerCase().replace(/^.*\./, '');
  const passed = isAppointmentPassed(appointment);
  const startAllowed = canStartExamNow(appointment);
  const canStart =
    ['scheduled', 'confirmed', 'in_progress'].includes(rawStatus) &&
    (rawStatus === 'in_progress' || startAllowed);
  const canCancelAppointment =
    canWrite &&
    !status.isLocked &&
    (rawStatus === 'scheduled' || rawStatus === 'confirmed');
  const canModify = canWrite && canModifyAppointment(appointment);
  const startBlockedReason = passed
    ? t('messages.startTooLate')
    : t('messages.startTooEarly');

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('details.title', { defaultValue: 'Appointment details' })}
        onClick={(event) => event.stopPropagation()}
        className="flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      >
        <header className="flex items-start justify-between border-b border-gray-200 p-5 dark:border-gray-700">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
              {t('details.title', { defaultValue: 'Appointment details' })}
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {appointment.patient_name} {appointment.patient_last_name}
            </h2>
            {appointment.patient_mrn && (
              <p className="mt-1 text-sm text-slate-500">
                MRN {appointment.patient_mrn}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('modal.discard')}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700"
          >
            <FiX size={20} />
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <div className="flex flex-wrap gap-2">
            <span className={`rounded-lg border px-2.5 py-1 text-xs font-semibold ${status.badgeClass}`}>
              {status.label}
            </span>
            {priority && (
              <span className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${priority.className}`}>
                {priority.label}
              </span>
            )}
            {appointment.source === 'mwl_import' && (
              <span className="rounded-lg bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
                MWL
              </span>
            )}
          </div>

          <dl className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            <Detail
              icon={<FiCalendar />}
              label={t('modal.classification')}
              value={typeLabel}
            />
            <Detail
              icon={<FiClock />}
              label={t('modal.timeSlot')}
              value={`${formatAppointmentClock(appointment.start_time)} · ${
                appointment.duration_minutes || 30
              } min`}
            />
            <Detail
              icon={<FiUser />}
              label={t('modal.assignTo')}
              value={assignee}
            />
            {appointment.accession_number && (
              <Detail
                label="Accession"
                value={appointment.accession_number}
                mono
              />
            )}
          </dl>

          {appointment.reason && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t('modal.notes')}
              </h3>
              <p className="mt-2 rounded-xl bg-slate-50 p-3 text-sm leading-relaxed text-slate-700 dark:bg-gray-700 dark:text-gray-200">
                {appointment.reason}
              </p>
            </section>
          )}
        </div>

        <footer className="flex flex-col gap-2 border-t border-gray-200 p-5 dark:border-gray-700">
          {!canStart && ['scheduled', 'confirmed'].includes(rawStatus) && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {startBlockedReason}
            </p>
          )}
          <div className="flex gap-2">
            {canModify && onModify && (
              <Button
                variant="secondary"
                size="md"
                className="gap-2"
                onClick={() => {
                  onClose();
                  onModify(appointment);
                }}
              >
                <FiEdit2 size={15} />
                {t('worklist.modify')}
              </Button>
            )}
            {rawStatus === 'in_progress' ||
            (canStart &&
              (rawStatus === 'scheduled' || rawStatus === 'confirmed') &&
              !passed) ? (
              <Button
                variant="primary"
                size="md"
                className="flex-1 gap-2"
                title={
                  rawStatus === 'in_progress'
                    ? t('worklist.resume')
                    : t('startExam')
                }
                onClick={() => {
                  onClose();
                  onStart(appointment);
                }}
              >
                <FiPlay size={15} />
                {rawStatus === 'in_progress'
                  ? t('worklist.resume')
                  : t('startExam')}
              </Button>
            ) : null}
            {canCancelAppointment && (
              <Button
                variant="outline"
                size="md"
                className="gap-2 text-red-500"
                onClick={() => {
                  onClose();
                  onCancel(String(appointment.id));
                }}
              >
                <FiTrash size={15} />
                {t('cancel')}
              </Button>
            )}
          </div>
        </footer>
      </aside>
    </div>
  );
}

function Detail({
  icon,
  label,
  value,
  mono = false,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-gray-700 dark:text-gray-300">
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {label}
        </dt>
        <dd
          className={`mt-0.5 truncate text-sm font-semibold text-slate-800 dark:text-gray-100 ${
            mono ? 'font-mono text-blue-600 dark:text-blue-400' : ''
          }`}
        >
          {value}
        </dd>
      </div>
    </div>
  );
}
